/**
 * User Management — requires 'users' permission.
 * Owner can manage permissions for all non-owner users via the Manage Permissions modal.
 * Admins can manage user accounts (role/active/pw reset) for member accounts.
 * All consequential changes are behind the ⋯ menu with confirmation steps.
 */

import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Icon } from '../components/Icon'
import { useAuth, authHeaders, type Permissions, type PermKey } from '../lib/auth'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

interface UserRow {
  user_id:         number
  email:           string
  display_name:    string
  role:            'owner' | 'admin' | 'member'
  active:          boolean
  force_pw_change: boolean
  last_login_at:   string | null
  created_at:      string
}

const ROLE_BADGE: Record<string, string> = {
  owner:  'pill bg-plum-100 text-plum-700',
  admin:  'pill bg-royal-100 text-royal-700',
  member: 'pill bg-ink-100 text-ink-500',
}

function fmtDate(s: string | null) {
  if (!s) return 'Never'
  return new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

// ── Permission config ────────────────────────────────────────────────────────

interface PermDef {
  key:     PermKey
  label:   string
  sub?:    boolean   // indented sub-permission
}

interface PermSection {
  title: string
  perms: PermDef[]
}

const PERM_SECTIONS: PermSection[] = [
  {
    title: 'Workspace',
    perms: [
      { key: 'pulse', label: 'Pulse (KPIs & revenue)' },
      { key: 'ask',   label: 'Ask AI' },
    ],
  },
  {
    title: 'Analytics',
    perms: [
      { key: 'cash',        label: 'Cash view' },
      { key: 'ships',       label: 'Shipments' },
      { key: 'ships_profit', label: 'Profit column (within Shipments)', sub: true },
      { key: 'customers',   label: 'Customers' },
      { key: 'pricing',     label: 'Pricing' },
    ],
  },
  {
    title: 'Operations',
    perms: [
      { key: 'affiliates', label: 'Affiliates' },
      { key: 'invoices',   label: 'Invoices' },
      { key: 'recon',      label: 'Reconciliation' },
      { key: 'disputes',   label: 'Disputes' },
      { key: 'claims',     label: 'Carrier Claims' },
    ],
  },
  {
    title: 'People & Costs',
    perms: [
      { key: 'personnel', label: 'Personnel roster' },
      { key: 'costs',     label: 'Operating Costs' },
    ],
  },
  {
    title: 'System',
    perms: [
      { key: 'users',    label: 'User Management' },
      { key: 'uploads',  label: 'Activity / Uploads' },
      { key: 'exports',  label: 'Excel Exports' },
      { key: 'settings', label: 'Settings' },
    ],
  },
  {
    title: 'Actions (what they can change)',
    perms: [
      { key: 'manage_rebills',      label: 'Rebills — detect, bill, send, revert' },
      { key: 'void_invoices',       label: 'Void customer invoices' },
      { key: 'manage_billing_mode', label: 'Change customer billing mode' },
      { key: 'edit_cash',           label: 'Edit / categorize cash transactions' },
      { key: 'upload_files',        label: 'Upload carrier / ByeLabel files' },
      { key: 'resolve_disputes',    label: 'Create / edit / resolve disputes' },
      { key: 'manage_claims',       label: 'Claims — edit, send carrier credits to customers' },
    ],
  },
]

// ── Permissions Modal ────────────────────────────────────────────────────────

function PermissionsModal({ user, onClose }: { user: UserRow; onClose: () => void }) {
  const { token } = useAuth()
  const qc = useQueryClient()

  // Fetch current permissions
  const { data: permsData, isLoading } = useQuery<Record<string, boolean>>({
    queryKey: ['user-perms', user.user_id],
    queryFn:  () => fetch(`/api/users/${user.user_id}/permissions`, { headers: authHeaders(token) }).then(r => r.json()),
  })

  // Map DB column names back to short perm keys for the toggle state
  const dbColToKey: Record<string, PermKey> = {
    can_view_pulse:           'pulse',
    can_view_ask:             'ask',
    can_view_cash:            'cash',
    can_view_shipments:       'ships',
    can_view_shipment_profit: 'ships_profit',
    can_view_customers:       'customers',
    can_view_pricing:         'pricing',
    can_view_affiliates:      'affiliates',
    can_view_invoices:        'invoices',
    can_view_reconciliation:  'recon',
    can_view_disputes:        'disputes',
    can_view_claims:          'claims',
    can_view_personnel:       'personnel',
    can_view_costs:           'costs',
    can_view_users:           'users',
    can_view_uploads:         'uploads',
    can_view_exports:         'exports',
    can_view_settings:        'settings',
    can_manage_rebills:       'manage_rebills',
    can_void_invoices:        'void_invoices',
    can_manage_billing_mode:  'manage_billing_mode',
    can_edit_cash:            'edit_cash',
    can_upload_files:         'upload_files',
    can_resolve_disputes:     'resolve_disputes',
    can_manage_claims:        'manage_claims',
  }

  function parsePerms(raw: Record<string, boolean>): Partial<Permissions> {
    const out: Partial<Permissions> = {}
    for (const [col, key] of Object.entries(dbColToKey)) {
      if (col in raw) (out as Record<string, boolean>)[key] = raw[col]
    }
    return out
  }

  const [draft, setDraft] = useState<Partial<Permissions> | null>(null)
  const [saved, setSaved]  = useState(false)

  // Initialise draft from fetched data (only once)
  const current = permsData ? parsePerms(permsData) : null
  const working = draft ?? current ?? {}

  const toggle = (key: PermKey) =>
    setDraft(prev => ({ ...(prev ?? working), [key]: !(working[key] ?? false) }))

  const save = useMutation({
    mutationFn: async () => {
      if (!draft) return
      const res = await fetch(`/api/users/${user.user_id}/permissions`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
        body:    JSON.stringify(draft),
      })
      if (!res.ok) { const d = await res.json(); throw new Error(d.error) }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-perms', user.user_id] })
      setSaved(true)
      setTimeout(() => { setSaved(false); setDraft(null) }, 1500)
    },
  })

  const hasChanges = draft !== null

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200 shrink-0">
          <div>
            <h2 className="text-[15px] font-semibold">Manage permissions</h2>
            <p className="text-[12px] text-ink-500 mt-0.5">
              {user.display_name} · <span className={ROLE_BADGE[user.role] ?? 'pill'}>{user.role}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><Icon name="x" /></button>
        </div>

        {/* Note */}
        <div className="px-6 pt-3 pb-0 shrink-0">
          <p className="text-[11px] text-ink-400 bg-ink-50 rounded-lg px-3 py-2">
            Changes take effect on the user's next login. Toggles are individual — no group presets.
          </p>
        </div>

        {/* Permission sections */}
        <div className="overflow-y-auto px-6 py-3 flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-ink-400">
              <Icon name="loader-circle" className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
          ) : (
            <div className="space-y-4">
              {PERM_SECTIONS.map(section => (
                <div key={section.title}>
                  <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wider mb-2">
                    {section.title}
                  </div>
                  <div className="space-y-1">
                    {section.perms.map(({ key, label, sub }) => {
                      const enabled = working[key] ?? false
                      // ships_profit is only meaningful if ships is enabled
                      const dimmed = key === 'ships_profit' && !(working['ships'] ?? false)
                      return (
                        <label
                          key={key}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-ink-50 transition-colors
                            ${sub ? 'ml-4 border-l-2 border-ink-100 pl-4' : ''}
                            ${dimmed ? 'opacity-40 pointer-events-none' : ''}`}
                        >
                          {/* Toggle */}
                          <button
                            type="button"
                            onClick={() => toggle(key)}
                            className={`relative w-9 h-5 rounded-full transition-colors shrink-0 focus:outline-none
                              ${enabled ? 'bg-royal-500' : 'bg-ink-200'}`}
                          >
                            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform
                              ${enabled ? 'translate-x-4' : 'translate-x-0'}`}
                            />
                          </button>
                          <span className={`text-[13px] ${enabled ? 'text-ink-900' : 'text-ink-500'}`}>
                            {label}
                          </span>
                          {dimmed && (
                            <span className="text-[10px] text-ink-400 ml-auto">requires Shipments</span>
                          )}
                        </label>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-ink-200 shrink-0">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <div className="flex items-center gap-2">
            {saved && (
              <span className="text-[12px] text-mint-600 flex items-center gap-1">
                <Icon name="check" className="w-3.5 h-3.5" /> Saved
              </span>
            )}
            {save.isError && (
              <span className="text-[12px] text-flame-600">
                {(save.error as Error)?.message ?? 'Save failed'}
              </span>
            )}
            <button
              onClick={() => save.mutate()}
              disabled={!hasChanges || save.isPending || saved}
              className="btn-primary"
            >
              {save.isPending ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Add User Modal ────────────────────────────────────────────────────────────

function AddUserModal({ onClose }: { onClose: () => void }) {
  const { token, isOwner } = useAuth()
  const qc = useQueryClient()
  const [form, setForm] = useState({ email: '', display_name: '', password: '', role: 'member' })
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/users', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
        body:    JSON.stringify(form),
      })
      if (!res.ok) { const d = await res.json(); throw new Error(d.error) }
      return res.json()
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); onClose() },
    onError:   (e: Error) => setError(e.message),
  })

  const set = (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm(f => ({ ...f, [k]: e.target.value }))

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200">
          <h2 className="text-[15px] font-semibold">Add user</h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><Icon name="x" /></button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="form-label">Display name *</label>
            <input className="form-input" value={form.display_name} onChange={set('display_name')} placeholder="Jane Smith" />
          </div>
          <div>
            <label className="form-label">Email *</label>
            <input className="form-input" type="email" value={form.email} onChange={set('email')} placeholder="jane@byelabel.com" />
          </div>
          <div>
            <label className="form-label">Temporary password * (user must change on first login)</label>
            <input className="form-input" type="text" value={form.password} onChange={set('password')} placeholder="Min 8 characters" />
          </div>
          <div>
            <label className="form-label">Role</label>
            <select className="form-select" value={form.role} onChange={set('role')}>
              <option value="member">Member</option>
              <option value="admin">Admin</option>
              {isOwner && <option value="owner">Owner</option>}
            </select>
            <p className="text-[11px] text-ink-400 mt-1">
              Admin defaults: all permissions on. Member defaults: restricted.
              You can customise permissions after creation.
            </p>
          </div>
          {error && (
            <div className="text-sm text-flame-700 bg-flame-50 border border-flame-200 rounded-xl px-3 py-2">{error}</div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-ink-200">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button
            onClick={() => create.mutate()}
            disabled={!form.email || !form.display_name || !form.password || create.isPending}
            className="btn-primary"
          >
            {create.isPending ? 'Creating…' : 'Create user'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── User ⋯ menu ────────────────────────────────────────────────────────────────

type MenuState = 'idle' | 'role-prompt' | 'confirm-deactivate' | 'confirm-reactivate'

function UserMenu({
  user,
  onManagePerms,
}: {
  user: UserRow
  onManagePerms: () => void
}) {
  const { token, user: me, isOwner, startViewAs } = useAuth()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [menu, setMenu] = useState<MenuState>('idle')
  const [newRole, setNewRole] = useState<string>(user.role)

  const isSelf  = user.user_id === Number(me?.sub)
  const canEdit = !isSelf && (isOwner || user.role !== 'owner')

  const patch = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const res = await fetch(`/api/users/${user.user_id}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
        body:    JSON.stringify(body),
      })
      if (!res.ok) { const d = await res.json(); throw new Error(d.error) }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      setMenu('idle'); setOpen(false)
    },
  })

  const close = () => { setMenu('idle'); setOpen(false) }

  // Close the menu on an outside click or Escape. The wrapper stops click
  // propagation (so a click inside never bubbles to the row), which means the
  // menu had no way to dismiss itself — this listens on the document instead.
  const wrapRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onPointer = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) close()
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!canEdit) return null

  return (
    <div ref={wrapRef} className="relative" onClick={e => e.stopPropagation()}>
      <button
        onClick={() => { setOpen(o => !o); setMenu('idle') }}
        className="p-1.5 rounded-lg hover:bg-ink-50 text-ink-400 hover:text-ink-700"
      >
        <Icon name="more-horizontal" />
      </button>

      {/* Main dropdown */}
      {open && menu === 'idle' && (
        <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-xl shadow-lg border border-ink-200 py-1 z-20">
          {/* Manage permissions — owner only, non-owner targets */}
          {isOwner && user.role !== 'owner' && (
            <button
              onClick={() => { close(); onManagePerms() }}
              className="w-full text-left px-3 py-2 text-[13px] text-ink-700 hover:bg-ink-50 flex items-center gap-2"
            >
              <Icon name="sliders-horizontal" className="w-3.5 h-3.5 text-ink-400" />
              Manage permissions…
            </button>
          )}
          {/* View as — owner only, active non-owner targets; read-only */}
          {isOwner && user.role !== 'owner' && user.active && (
            <button
              onClick={async () => {
                close()
                const r = await startViewAs(user.user_id)
                if (r.ok) { qc.clear(); navigate('/') }
                else window.alert(`Could not view as ${user.display_name}: ${r.error}`)
              }}
              className="w-full text-left px-3 py-2 text-[13px] text-ink-700 hover:bg-ink-50 flex items-center gap-2"
            >
              <Icon name="eye" className="w-3.5 h-3.5 text-ink-400" />
              View as (read-only)
            </button>
          )}
          <button
            onClick={() => { setMenu('role-prompt'); setNewRole(user.role) }}
            className="w-full text-left px-3 py-2 text-[13px] text-ink-700 hover:bg-ink-50"
          >
            Change role…
          </button>
          <button
            onClick={() => patch.mutate({ force_pw_change: true })}
            className="w-full text-left px-3 py-2 text-[13px] text-ink-700 hover:bg-ink-50"
          >
            Force password reset
          </button>
          <div className="border-t border-ink-100 mt-1 pt-1">
            {user.active
              ? <button onClick={() => setMenu('confirm-deactivate')} className="w-full text-left px-3 py-2 text-[13px] text-flame-600 hover:bg-flame-50">Deactivate…</button>
              : <button onClick={() => setMenu('confirm-reactivate')} className="w-full text-left px-3 py-2 text-[13px] text-mint-700 hover:bg-mint-50">Reactivate…</button>
            }
          </div>
        </div>
      )}

      {/* Role change sub-panel */}
      {open && menu === 'role-prompt' && (
        <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-xl shadow-lg border border-ink-200 p-3 z-20">
          <label className="form-label mb-2">New role</label>
          <select className="form-select mb-3" value={newRole} onChange={e => setNewRole(e.target.value)}>
            <option value="member">Member</option>
            <option value="admin">Admin</option>
            {isOwner && <option value="owner">Owner</option>}
          </select>
          <div className="flex gap-2">
            <button onClick={close} className="btn-ghost text-xs flex-1">Cancel</button>
            <button
              onClick={() => patch.mutate({ role: newRole })}
              disabled={patch.isPending || newRole === user.role}
              className="btn-primary text-xs flex-1"
            >
              {patch.isPending ? '…' : 'Apply'}
            </button>
          </div>
        </div>
      )}

      {/* Deactivate confirmation */}
      {open && menu === 'confirm-deactivate' && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl shadow-lg border border-ink-200 p-3 z-20">
          <p className="text-[13px] text-ink-700 mb-3">Deactivate <b>{user.display_name}</b>? They will no longer be able to sign in.</p>
          <div className="flex gap-2">
            <button onClick={close} className="btn-ghost text-xs flex-1">Cancel</button>
            <button
              onClick={() => patch.mutate({ active: false })}
              disabled={patch.isPending}
              className="flex-1 text-xs font-medium px-3 py-1.5 rounded-lg bg-flame-600 text-white hover:bg-flame-700"
            >
              {patch.isPending ? '…' : 'Deactivate'}
            </button>
          </div>
        </div>
      )}

      {/* Reactivate confirmation */}
      {open && menu === 'confirm-reactivate' && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl shadow-lg border border-ink-200 p-3 z-20">
          <p className="text-[13px] text-ink-700 mb-3">Reactivate <b>{user.display_name}</b>?</p>
          <div className="flex gap-2">
            <button onClick={close} className="btn-ghost text-xs flex-1">Cancel</button>
            <button
              onClick={() => patch.mutate({ active: true })}
              disabled={patch.isPending}
              className="flex-1 text-xs font-medium px-3 py-1.5 rounded-lg bg-mint-600 text-white hover:bg-mint-700"
            >
              {patch.isPending ? '…' : 'Reactivate'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Main view ─────────────────────────────────────────────────────────────────

export function UsersView() {
  const { t } = useTranslation()
  const { token, isOwner } = useAuth()
  const [showAdd, setShowAdd] = useState(false)
  const [permTarget, setPermTarget] = useState<UserRow | null>(null)

  const { data: users = [], isLoading } = useQuery<UserRow[]>({
    queryKey: ['users'],
    queryFn: () => fetch('/api/users', { headers: authHeaders(token) }).then(r => r.json()),
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">User Management</h1>
          <p className="text-sm text-ink-500 mt-0.5">{users.filter(u => u.active).length} active users</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn-primary flex items-center gap-2">
          <Icon name="user-plus" className="w-4 h-4" /> Add user
        </button>
      </div>

      {isOwner && (
        <div className="text-[12px] text-ink-500 bg-ink-50 rounded-xl px-4 py-2.5">
          <b>Owner:</b> Use the ⋯ menu → "Manage permissions…" to control exactly what each user can see.
          Changes take effect on the user's next login.
        </div>
      )}

      <div className="bg-white rounded-2xl border border-ink-200 overflow-visible">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-ink-400">
            <Icon name="loader-circle" className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-ink-100 text-left text-[11px] font-semibold text-ink-500 uppercase tracking-wide">
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Last login</th>
                <th className="px-4 py-3">Joined</th>
                <th className="px-4 py-3 w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {users.map(u => (
                <tr key={u.user_id} className="hover:bg-ink-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-ink-900">{u.display_name}</div>
                    <div className="text-ink-400 text-[11px]">{u.email}</div>
                    {u.force_pw_change && (
                      <span className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full">pw reset required</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={ROLE_BADGE[u.role] ?? 'pill'}>{u.role}</span>
                  </td>
                  <td className="px-4 py-3">
                    {u.active
                      ? <span className="pill pill-mint">Active</span>
                      : <span className="pill bg-ink-100 text-ink-500">Inactive</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-ink-500">{fmtDate(u.last_login_at)}</td>
                  <td className="px-4 py-3 text-ink-500 mono">{u.created_at.split('T')[0]}</td>
                  <td className="px-4 py-3">
                    <UserMenu
                      user={u}
                      onManagePerms={() => setPermTarget(u)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd    && <AddUserModal onClose={() => setShowAdd(false)} />}
      {permTarget && <PermissionsModal user={permTarget} onClose={() => setPermTarget(null)} />}
    </div>
  )
}
